import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostDokumentHinterlegenRequest, IPostDokumentHinterlegenResponse } from 'fa-kt.types';
import { PostDokumentHinterlegenCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-dokument-hinterlegen',
  templateUrl: './post-dokument-hinterlegen.component.html',
  styleUrls: ['./post-dokument-hinterlegen.component.css'],
})
export class PostDokumentHinterlegenCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostDokumentHinterlegenRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostDokumentHinterlegenRequest> = new EventEmitter<IPostDokumentHinterlegenRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostDokumentHinterlegenResponse> = new EventEmitter<IPostDokumentHinterlegenResponse>();

  /* << fields    */
  /*    fields >> */

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postDokumentHinterlegen: PostDokumentHinterlegenCommandService /* << more    */,
  ) /*    more >> */
  {
    this.command = sitemap.DOKUMENTE.Commands.POST_DOKUMENT_HINTERLEGEN;
    this.form = this.fb.group({
      erforderlichesDokument: this.fb.group({
        id: [null, []],
        original: [null, []],
        gutachten: [null, []],
        name: [null, [Validators.required]],
        erforderlich: [null, []],
        fortschritt: [null, []],
        kommentar: [null, []],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postDokumentHinterlegen.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostDokumentHinterlegenRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
      this.erforderlichesDokumentId?.valueChanges.subscribe(async (id) => {
        /* << on-erforderliches-dokument-id-change    */
        /*    on-erforderliches-dokument-id-change >> */
      }) as Subscription,
      this.erforderlichesDokumentOriginal?.valueChanges.subscribe(async (original) => {
        /* << on-erforderliches-dokument-original-change    */
        /*    on-erforderliches-dokument-original-change >> */
      }) as Subscription,
      this.erforderlichesDokumentGutachten?.valueChanges.subscribe(async (gutachten) => {
        /* << on-erforderliches-dokument-gutachten-change    */
        /*    on-erforderliches-dokument-gutachten-change >> */
      }) as Subscription,
      this.erforderlichesDokumentName?.valueChanges.subscribe(async (name) => {
        /* << on-erforderliches-dokument-name-change    */
        /*    on-erforderliches-dokument-name-change >> */
      }) as Subscription,
      this.erforderlichesDokumentErforderlich?.valueChanges.subscribe(async (erforderlich) => {
        /* << on-erforderliches-dokument-erforderlich-change    */
        /*    on-erforderliches-dokument-erforderlich-change >> */
      }) as Subscription,
      this.erforderlichesDokumentFortschritt?.valueChanges.subscribe(async (fortschritt) => {
        /* << on-erforderliches-dokument-fortschritt-change    */
        /*    on-erforderliches-dokument-fortschritt-change >> */
      }) as Subscription,
      this.erforderlichesDokumentKommentar?.valueChanges.subscribe(async (kommentar) => {
        /* << on-erforderliches-dokument-kommentar-change    */
        /*    on-erforderliches-dokument-kommentar-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostDokumentHinterlegenRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.erforderlichesDokument) {
        value.erforderlichesDokument = cleanObject(value.erforderlichesDokument);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.erforderlichesDokument) {
        payload.erforderlichesDokument = cleanObject(payload.erforderlichesDokument);
      }
      const response: IPostDokumentHinterlegenResponse = await this.postDokumentHinterlegen.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get erforderlichesDokumentId() {
    return this.form.controls.erforderlichesDokument.get('id');
  }
  get erforderlichesDokumentOriginal() {
    return this.form.controls.erforderlichesDokument.get('original');
  }
  get erforderlichesDokumentGutachten() {
    return this.form.controls.erforderlichesDokument.get('gutachten');
  }
  get erforderlichesDokumentName() {
    return this.form.controls.erforderlichesDokument.get('name');
  }
  get erforderlichesDokumentErforderlich() {
    return this.form.controls.erforderlichesDokument.get('erforderlich');
  }
  get erforderlichesDokumentFortschritt() {
    return this.form.controls.erforderlichesDokument.get('fortschritt');
  }
  get erforderlichesDokumentKommentar() {
    return this.form.controls.erforderlichesDokument.get('kommentar');
  }
}
